import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { getApiBase, getAuthHeaders } from '../../utils/api';
import { OrganizationPicker } from '../common';

const AssignPackageModal = ({ isOpen, onClose, onSuccess, organizations = [], packages = [] }) => {
  const [selectedOrg, setSelectedOrg] = useState('');
  const [selectedPackage, setSelectedPackage] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Custom overrides
  const [showOverrides, setShowOverrides] = useState(false);
  const [customBasePrice, setCustomBasePrice] = useState('');
  const [customExecutiveCount, setCustomExecutiveCount] = useState('');
  const [customEaCount, setCustomEaCount] = useState('');
  const [expiresAt, setExpiresAt] = useState('');
  const [autoRenew, setAutoRenew] = useState(true);
  const [notes, setNotes] = useState('');

  // Markup override
  const [showMarkupOverride, setShowMarkupOverride] = useState(false);
  const [markupOverride, setMarkupOverride] = useState({
    flight_booking_fee_type: 'none',
    flight_booking_fee_flat: 0,
    flight_booking_fee_pct: 0,
    flight_booking_fee_min: 0,
    flight_booking_fee_max: 0,
    ancillary_markup_type: 'none',
    ancillary_markup_value: 0,
    hotel_booking_fee_type: 'none',
    hotel_booking_fee_flat: 0,
    hotel_booking_fee_pct: 0,
    hotel_booking_fee_min: 0,
    hotel_booking_fee_max: 0,
    hotel_commission_model: 'retain',
    flight_payment_method: 'duffel_payments',
    hotel_payment_method: 'balance',
    customer_payment_methods: ['credit_card'],
    booker_collection_method: 'credit_card',
    fee_billing_mode: 'at_booking',
    show_booking_fee_separately: false,
    booking_fee_label: 'Booking Fee',
    fee_currency: 'USD',
  });

  // Payment type
  const [paymentType, setPaymentType] = useState('credit_card');

  // Current assignment info
  const [currentAssignment, setCurrentAssignment] = useState(null);

  useEffect(() => {
    if (isOpen) {
      setSelectedOrg('');
      setSelectedPackage('');
      setError('');
      setShowOverrides(false);
      setCustomBasePrice('');
      setCustomExecutiveCount('');
      setCustomEaCount('');
      setExpiresAt('');
      setAutoRenew(true);
      setNotes('');
      setCurrentAssignment(null);
      setShowMarkupOverride(false);
      setPaymentType('credit_card');
      setMarkupOverride({
        flight_booking_fee_type: 'none',
        flight_booking_fee_flat: 0,
        flight_booking_fee_pct: 0,
        flight_booking_fee_min: 0,
        flight_booking_fee_max: 0,
        ancillary_markup_type: 'none',
        ancillary_markup_value: 0,
        hotel_booking_fee_type: 'none',
        hotel_booking_fee_flat: 0,
        hotel_booking_fee_pct: 0,
        hotel_booking_fee_min: 0,
        hotel_booking_fee_max: 0,
        hotel_commission_model: 'retain',
        flight_payment_method: 'duffel_payments',
        hotel_payment_method: 'balance',
        show_booking_fee_separately: false,
        booking_fee_label: 'Booking Fee',
        fee_currency: 'USD',
      });
    }
  }, [isOpen]);

  // Load current assignment when organization is selected
  useEffect(() => {
    if (selectedOrg) {
      loadCurrentAssignment(selectedOrg);
    } else {
      setCurrentAssignment(null);
    }
  }, [selectedOrg]);

  const loadCurrentAssignment = async (orgId) => {
    try {
      const apiBase = getApiBase();
      const headers = getAuthHeaders();
      const response = await axios.get(`${apiBase}/organizations/${orgId}/package`, { headers });

      if (response.data.has_package) {
        setCurrentAssignment(response.data.package);
      } else {
        setCurrentAssignment(null);
      }
    } catch (err) {
      console.error('Failed to load current assignment:', err);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!selectedOrg || !selectedPackage) {
      setError('Please select both an organization and a package');
      return;
    }

    setLoading(true);

    try {
      const apiBase = getApiBase();
      const headers = getAuthHeaders();

      const assignmentData = {
        organization_id: selectedOrg,
        package_id: selectedPackage,
        auto_renew: autoRenew,
        payment_type: paymentType,
        notes: notes || null
      };

      if (customBasePrice) {
        assignmentData.custom_base_price = parseFloat(customBasePrice);
      }
      if (customExecutiveCount) {
        assignmentData.custom_executive_count = parseInt(customExecutiveCount);
      }
      if (customEaCount) {
        assignmentData.custom_ea_count = parseInt(customEaCount);
      }
      if (expiresAt) {
        assignmentData.expires_at = new Date(expiresAt).toISOString();
      }
      if (showMarkupOverride) {
        assignmentData.custom_booking_markup_config = markupOverride;
      }

      await axios.post(`${apiBase}/admin/package-assignments`, assignmentData, { headers });

      onSuccess();
      onClose();
    } catch (err) {
      setError(err.response?.data?.detail || err.message || 'Failed to assign package');
    } finally {
      setLoading(false);
    }
  };

  const selectedPackageDetails = packages.find(p => p.id === selectedPackage);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[70]">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4">
        <div className="border-b border-gray-200 px-6 py-4">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-bold text-gray-900">Assign Package to Organization</h2>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 text-2xl"
            >
              &times;
            </button>
          </div>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="px-6 py-4 space-y-4 max-h-[60vh] overflow-y-auto">
            {error && (
              <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
                {error}
              </div>
            )}

            {/* Organization Selection */}
            <div>
              <OrganizationPicker
                organizations={organizations}
                value={selectedOrg}
                onChange={(orgId) => setSelectedOrg(orgId)}
                placeholder="Select an organization..."
                label="Organization"
                required
                showDomain
                showUserCount
              />
            </div>

            {/* Current Assignment Info */}
            {currentAssignment && (
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                <div className="flex items-start">
                  <svg className="w-5 h-5 text-yellow-400 mt-0.5 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.664-.833-2.464 0L3.34 16.5c-.77.833.192 2.5 1.732 2.5z" />
                  </svg>
                  <div>
                    <h4 className="text-sm font-medium text-yellow-800">Current Package</h4>
                    <p className="text-sm text-yellow-700 mt-1">
                      This organization currently has the <strong>{currentAssignment.name}</strong> package.
                      Assigning a new package will cancel the current assignment.
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Package Selection */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Package *
              </label>
              <select
                value={selectedPackage}
                onChange={(e) => setSelectedPackage(e.target.value)}
                className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-purple-500 focus:border-purple-500"
                required
              >
                <option value="">Select a package...</option>
                {packages.filter(p => p.status === 'active').map(pkg => (
                  <option key={pkg.id} value={pkg.id}>
                    {pkg.name} - {pkg.base_price} {pkg.currency}/month
                    {pkg.is_trial ? ' (Trial)' : ''}
                  </option>
                ))}
              </select>
            </div>

            {/* Package Details */}
            {selectedPackageDetails && (
              <div className="bg-gray-50 rounded-lg p-4">
                <h4 className="font-medium text-gray-900 mb-2">Package Details</h4>
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <div className="text-gray-600">Base Price:</div>
                  <div>{selectedPackageDetails.base_price} {selectedPackageDetails.currency}</div>

                  <div className="text-gray-600">Teams:</div>
                  <div>{selectedPackageDetails.teams_enabled ? 'Yes' : 'No'}</div>

                  <div className="text-gray-600">Flight Alerts:</div>
                  <div>{selectedPackageDetails.flight_alerts_enabled ? 'Yes' : 'No'}</div>

                  <div className="text-gray-600">SSO:</div>
                  <div>{selectedPackageDetails.sso_enabled ? 'Yes' : 'No'}</div>

                  {selectedPackageDetails.is_trial && (
                    <>
                      <div className="text-gray-600">Trial Duration:</div>
                      <div>{selectedPackageDetails.trial_days || 14} days</div>
                    </>
                  )}
                </div>
              </div>
            )}

            {/* Custom Overrides Toggle */}
            <div>
              <button
                type="button"
                onClick={() => setShowOverrides(!showOverrides)}
                className="flex items-center text-sm text-purple-600 hover:text-purple-800"
              >
                <svg
                  className={`w-4 h-4 mr-1 transform transition-transform ${showOverrides ? 'rotate-90' : ''}`}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
                Custom Overrides (Optional)
              </button>
            </div>

            {/* Custom Override Fields */}
            {showOverrides && (
              <div className="bg-gray-50 rounded-lg p-4 space-y-4">
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Custom Base Price
                    </label>
                    <input
                      type="number"
                      step="0.01"
                      value={customBasePrice}
                      onChange={(e) => setCustomBasePrice(e.target.value)}
                      placeholder={selectedPackageDetails?.base_price?.toString() || '0'}
                      className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Custom Executive Count
                    </label>
                    <input
                      type="number"
                      value={customExecutiveCount}
                      onChange={(e) => setCustomExecutiveCount(e.target.value)}
                      className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Custom EA Count
                    </label>
                    <input
                      type="number"
                      value={customEaCount}
                      onChange={(e) => setCustomEaCount(e.target.value)}
                      className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Expiration Date (optional)
                  </label>
                  <input
                    type="datetime-local"
                    value={expiresAt}
                    onChange={(e) => setExpiresAt(e.target.value)}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                  />
                </div>

                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={autoRenew}
                    onChange={(e) => setAutoRenew(e.target.checked)}
                    className="h-4 w-4 text-purple-600 border-gray-300 rounded"
                  />
                  <span className="ml-2 text-sm text-gray-700">Auto-renew at end of period</span>
                </label>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Payment Type</label>
                  <select
                    value={paymentType}
                    onChange={(e) => setPaymentType(e.target.value)}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                  >
                    <option value="credit_card">Credit Card</option>
                    <option value="direct_bill">Direct Bill (Invoice)</option>
                    <option value="ach">ACH (Bank Transfer via Stripe)</option>
                  </select>
                  {paymentType === 'ach' && (
                    <p className="text-xs text-blue-600 mt-1">
                      ACH payments are processed via Stripe. Ensure the organization has a bank account configured in Billing &amp; Finance settings.
                    </p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Notes
                  </label>
                  <textarea
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    placeholder="Internal notes about this assignment..."
                    className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                    rows={2}
                  />
                </div>

                {/* Markup Override */}
                <div className="border-t border-gray-200 pt-4">
                  <label className="flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={showMarkupOverride}
                      onChange={(e) => {
                        const checked = e.target.checked;
                        setShowMarkupOverride(checked);
                        // Initialize with selected package's markup config if available
                        if (checked && selectedPackageDetails?.booking_markup_config) {
                          const c = selectedPackageDetails.booking_markup_config;
                          setMarkupOverride({
                            flight_booking_fee_type: c.flight_booking_fee_type || c.flight_markup_type || 'none',
                            flight_booking_fee_flat: c.flight_booking_fee_flat || (c.flight_markup_type === 'flat' ? c.flight_markup_value : 0) || 0,
                            flight_booking_fee_pct: c.flight_booking_fee_pct || (c.flight_markup_type === 'percentage' ? c.flight_markup_value : 0) || 0,
                            flight_booking_fee_min: c.flight_booking_fee_min || 0,
                            flight_booking_fee_max: c.flight_booking_fee_max || 0,
                            ancillary_markup_type: c.ancillary_markup_type || 'none',
                            ancillary_markup_value: c.ancillary_markup_value || 0,
                            hotel_booking_fee_type: c.hotel_booking_fee_type || c.hotel_markup_type || 'none',
                            hotel_booking_fee_flat: c.hotel_booking_fee_flat || (c.hotel_markup_type === 'flat' ? c.hotel_markup_value : 0) || 0,
                            hotel_booking_fee_pct: c.hotel_booking_fee_pct || (c.hotel_markup_type === 'percentage' ? c.hotel_markup_value : 0) || 0,
                            hotel_booking_fee_min: c.hotel_booking_fee_min || 0,
                            hotel_booking_fee_max: c.hotel_booking_fee_max || 0,
                            hotel_commission_model: c.hotel_commission_model || 'retain',
                            flight_payment_method: c.flight_payment_method || 'duffel_payments',
                            hotel_payment_method: c.hotel_payment_method || 'balance',
                            show_booking_fee_separately: c.show_booking_fee_separately || c.show_markup_as_separate_line || false,
                            booking_fee_label: c.booking_fee_label || c.markup_label || 'Booking Fee',
                            fee_currency: c.fee_currency || c.booking_fee_currency || 'USD',
                          });
                        }
                      }}
                      className="h-4 w-4 text-purple-600 border-gray-300 rounded"
                    />
                    <span className="ml-2 text-sm font-medium text-gray-700">Override Booking Markup</span>
                  </label>
                  <p className="text-xs text-gray-500 mt-1 ml-6">
                    Set custom markup/service fees for this organization instead of using the package defaults.
                  </p>
                </div>

                {showMarkupOverride && (
                  <div className="bg-white border border-gray-200 rounded-lg p-4 space-y-4">
                    {/* Flight Booking Fee */}
                    <h5 className="text-sm font-medium text-gray-800">Flight Booking Fee</h5>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">Type</label>
                        <select
                          value={markupOverride.flight_booking_fee_type || 'none'}
                          onChange={(e) => setMarkupOverride(prev => ({ ...prev, flight_booking_fee_type: e.target.value }))}
                          className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                        >
                          <option value="none">No Fee</option>
                          <option value="flat">Flat Amount</option>
                          <option value="percentage">Percentage</option>
                          <option value="both">Flat + Percentage</option>
                        </select>
                      </div>
                      {(markupOverride.flight_booking_fee_type === 'flat' || markupOverride.flight_booking_fee_type === 'both') && (
                        <div>
                          <label className="block text-xs font-medium text-gray-600 mb-1">Flat Amount</label>
                          <input type="number" step="0.01"
                            value={markupOverride.flight_booking_fee_flat || 0}
                            onChange={(e) => setMarkupOverride(prev => ({ ...prev, flight_booking_fee_flat: parseFloat(e.target.value) || 0 }))}
                            className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm" />
                        </div>
                      )}
                      {(markupOverride.flight_booking_fee_type === 'percentage' || markupOverride.flight_booking_fee_type === 'both') && (
                        <div>
                          <label className="block text-xs font-medium text-gray-600 mb-1">Percentage (%)</label>
                          <input type="number" step="0.1"
                            value={markupOverride.flight_booking_fee_pct || 0}
                            onChange={(e) => setMarkupOverride(prev => ({ ...prev, flight_booking_fee_pct: parseFloat(e.target.value) || 0 }))}
                            className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm" />
                        </div>
                      )}
                    </div>

                    {/* Ancillary Markup */}
                    <h5 className="text-sm font-medium text-gray-800">Ancillary Markup (bags, seats, etc.)</h5>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">Type</label>
                        <select
                          value={markupOverride.ancillary_markup_type || 'none'}
                          onChange={(e) => setMarkupOverride(prev => ({ ...prev, ancillary_markup_type: e.target.value }))}
                          className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                        >
                          <option value="none">No Markup</option>
                          <option value="percentage">Percentage</option>
                          <option value="flat">Flat Amount (per item)</option>
                        </select>
                      </div>
                      {markupOverride.ancillary_markup_type !== 'none' && (
                        <div>
                          <label className="block text-xs font-medium text-gray-600 mb-1">
                            {markupOverride.ancillary_markup_type === 'percentage' ? 'Percentage (%)' : 'Flat Amount'}
                          </label>
                          <input type="number" step="0.01"
                            value={markupOverride.ancillary_markup_value || 0}
                            onChange={(e) => setMarkupOverride(prev => ({ ...prev, ancillary_markup_value: parseFloat(e.target.value) || 0 }))}
                            className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm" />
                        </div>
                      )}
                    </div>

                    {/* Hotel Booking Fee */}
                    <h5 className="text-sm font-medium text-gray-800">Hotel Booking Fee</h5>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">Type</label>
                        <select
                          value={markupOverride.hotel_booking_fee_type || 'none'}
                          onChange={(e) => setMarkupOverride(prev => ({ ...prev, hotel_booking_fee_type: e.target.value }))}
                          className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                        >
                          <option value="none">No Fee</option>
                          <option value="flat">Flat Amount</option>
                          <option value="percentage">Percentage</option>
                          <option value="both">Flat + Percentage</option>
                        </select>
                      </div>
                      {(markupOverride.hotel_booking_fee_type === 'flat' || markupOverride.hotel_booking_fee_type === 'both') && (
                        <div>
                          <label className="block text-xs font-medium text-gray-600 mb-1">Flat Amount</label>
                          <input type="number" step="0.01"
                            value={markupOverride.hotel_booking_fee_flat || 0}
                            onChange={(e) => setMarkupOverride(prev => ({ ...prev, hotel_booking_fee_flat: parseFloat(e.target.value) || 0 }))}
                            className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm" />
                        </div>
                      )}
                      {(markupOverride.hotel_booking_fee_type === 'percentage' || markupOverride.hotel_booking_fee_type === 'both') && (
                        <div>
                          <label className="block text-xs font-medium text-gray-600 mb-1">Percentage (%)</label>
                          <input type="number" step="0.1"
                            value={markupOverride.hotel_booking_fee_pct || 0}
                            onChange={(e) => setMarkupOverride(prev => ({ ...prev, hotel_booking_fee_pct: parseFloat(e.target.value) || 0 }))}
                            className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm" />
                        </div>
                      )}
                    </div>

                    {/* Payment Methods */}
                    <h5 className="text-sm font-medium text-gray-800">Payment Methods</h5>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">Flights</label>
                        <select
                          value={markupOverride.flight_payment_method || 'duffel_payments'}
                          onChange={(e) => setMarkupOverride(prev => ({ ...prev, flight_payment_method: e.target.value }))}
                          className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                        >
                          <option value="duffel_payments">Customer Card</option>
                          <option value="balance">Duffel Balance</option>
                        </select>
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">Hotels</label>
                        <select
                          value={markupOverride.hotel_payment_method || 'balance'}
                          onChange={(e) => setMarkupOverride(prev => ({ ...prev, hotel_payment_method: e.target.value }))}
                          className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                        >
                          <option value="duffel_payments">Customer Card</option>
                          <option value="balance">Duffel Balance</option>
                        </select>
                      </div>
                    </div>

                    {/* Display Settings */}
                    <h5 className="text-sm font-medium text-gray-800">Display Settings</h5>
                    <label className="flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={markupOverride.show_booking_fee_separately || false}
                        onChange={(e) => setMarkupOverride(prev => ({ ...prev, show_booking_fee_separately: e.target.checked }))}
                        className="h-4 w-4 text-purple-600 border-gray-300 rounded"
                      />
                      <span className="ml-2 text-sm text-gray-700">Show as separate line item</span>
                    </label>
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">Fee Label</label>
                      <input
                        type="text"
                        value={markupOverride.booking_fee_label || 'Booking Fee'}
                        onChange={(e) => setMarkupOverride(prev => ({ ...prev, booking_fee_label: e.target.value }))}
                        placeholder="Booking Fee"
                        className="w-full sm:w-64 border border-gray-300 rounded-md px-3 py-2 text-sm"
                      />
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

          <div className="bg-gray-50 border-t border-gray-200 px-6 py-4 flex justify-end space-x-3">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
              disabled={loading}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-4 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700 disabled:opacity-50"
              disabled={loading}
            >
              {loading ? 'Assigning...' : 'Assign Package'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AssignPackageModal;
